import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Building2, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge, Empty, PageHeader } from "@/components/common";
import { ClientDialog } from "@/components/forms";
import { useList } from "@/lib/hooks";
import { CLIENT_TYPES } from "@/lib/constants";

export default function Clients() {
  const [params] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [type, setType] = useState("all");
  const [archived, setArchived] = useState(false);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const { items, reload } = useList("/clients", { q, type: type === "all" ? "" : type, archived });

  return (
    <>
      <PageHeader title="Clients" subtitle="Particuliers et professionnels">
        <Button onClick={() => setOpen(true)} className="dt-glow" data-testid="new-client-button"><Plus className="mr-1 h-4 w-4" />Nouveau client</Button>
      </PageHeader>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <Input placeholder="Rechercher (nom, société, ville, téléphone…)" value={q} onChange={(e) => setQ(e.target.value)} data-testid="clients-search-input" className="sm:max-w-sm" />
        <Tabs value={type} onValueChange={setType}>
          <TabsList>
            <TabsTrigger value="all" data-testid="clients-tab-all">Tous</TabsTrigger>
            <TabsTrigger value="particulier" data-testid="clients-tab-particulier">Particuliers</TabsTrigger>
            <TabsTrigger value="professionnel" data-testid="clients-tab-professionnel">Professionnels</TabsTrigger>
          </TabsList>
        </Tabs>
        <Button variant={archived ? "default" : "secondary"} size="sm" onClick={() => setArchived(!archived)} data-testid="clients-archived-toggle">
          {archived ? "Voir les clients actifs" : "Clients archivés"}
        </Button>
      </div>
      {items.length === 0 ? (
        <Empty testid="clients-empty">Aucun client pour le moment.</Empty>
      ) : (
        <div className="dt-card overflow-x-auto">
          <Table data-testid="clients-table">
            <TableHeader><TableRow>
              <TableHead>Client</TableHead><TableHead>Type</TableHead><TableHead>Téléphone</TableHead><TableHead>Email</TableHead><TableHead>Ville</TableHead><TableHead className="text-right">Interv.</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.map((c) => (
                <TableRow key={c.id} onClick={() => nav(`/clients/${c.id}`)} className="cursor-pointer" data-testid={`client-row-${c.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {c.type === "professionnel" ? <Building2 className="h-4 w-4 text-cyan-300" /> : <User className="h-4 w-4 text-blue-300" />}
                      <div><p className="font-medium text-white">{c.name}{c.archived && <span data-testid={`client-row-archived-badge-${c.id}`} className="ml-2 rounded-full border border-slate-400/40 px-2 py-0.5 text-xs text-slate-300">Archivé</span>}</p>{c.company && <p className="text-xs text-slate-400">{c.company}</p>}</div>
                    </div>
                  </TableCell>
                  <TableCell><Badge value={c.type} map={CLIENT_TYPES} /></TableCell>
                  <TableCell className="font-mono-dt text-xs">{c.phone || "—"}</TableCell>
                  <TableCell className="text-slate-400">{c.email || "—"}</TableCell>
                  <TableCell>{c.city || "—"}</TableCell>
                  <TableCell className="text-right font-mono-dt">{c.interventions_count}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <ClientDialog open={open} onOpenChange={setOpen} initial={{}} onSaved={(c) => nav(`/clients/${c.id}`)} />
    </>
  );
}
